// Browser-native image compression using Canvas API (no external library for standard formats).
// HEIC/HEIF triggers a dynamic import of heic2any for conversion before compression.

const HEIC_MAGIC = [
  [0x00, 0x00, 0x00, 0x18, 0x66, 0x74, 0x79, 0x70], // ftyp box
  [0x00, 0x00, 0x00, 0x1c, 0x66, 0x74, 0x79, 0x70],
]

async function isHeic(file) {
  const buf = await file.slice(0, 12).arrayBuffer()
  const bytes = new Uint8Array(buf)
  // Check for 'ftyp' at offset 4
  return bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70
}

async function fileToImageBitmap(file) {
  if (file.type === 'image/heic' || file.type === 'image/heif' || await isHeic(file)) {
    const heic2any = (await import('https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js')).default
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 })
    return createImageBitmap(Array.isArray(blob) ? blob[0] : blob)
  }
  return createImageBitmap(file)
}

/**
 * Compress an image file using Canvas API.
 * Returns a Blob at the target quality/dimensions.
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidthPx    = 1920,
    maxHeightPx   = 1080,
    targetSizeKb  = 800,
    quality       = 0.85,
    outputFormat  = 'image/jpeg',
  } = options

  const bitmap = await fileToImageBitmap(file)

  // Calculate scaled dimensions maintaining aspect ratio
  let { width, height } = bitmap
  if (width > maxWidthPx || height > maxHeightPx) {
    const ratio = Math.min(maxWidthPx / width, maxHeightPx / height)
    width  = Math.round(width  * ratio)
    height = Math.round(height * ratio)
  }

  const canvas = new OffscreenCanvas(width, height)
  const ctx    = canvas.getContext('2d')
  ctx.drawImage(bitmap, 0, 0, width, height)
  bitmap.close()

  // Iteratively reduce quality if output exceeds targetSizeKb
  let q    = quality
  let blob = await canvas.convertToBlob({ type: outputFormat, quality: q })

  for (let i = 0; i < 5 && blob.size > targetSizeKb * 1024; i++) {
    q   -= 0.05
    blob = await canvas.convertToBlob({ type: outputFormat, quality: Math.max(0.1, q) })
  }

  return blob
}

/**
 * Generate a 400×400 thumbnail (always JPEG, quality 0.7).
 */
export async function generateThumbnail(file) {
  return compressImage(file, {
    maxWidthPx:   400,
    maxHeightPx:  400,
    targetSizeKb: 80,
    quality:      0.7,
    outputFormat: 'image/jpeg',
  })
}
